import IntranetLayout from "@/components/IntranetLayout";
import { useState } from "react";
import { Users, Handshake, Building2, Target, Search, Plus, MessageSquare, FileText, Clock, AlertTriangle, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

type RelStatus = "active" | "warm" | "dormant" | "action_required";

interface Partner {
  id: string; name: string; type: "strategic" | "client" | "vendor" | "prospect"; category: string;
  status: RelStatus; owner: string; agreement?: { type: string; signed: string; expires: string; valid: boolean };
  workstreams: string[]; contacts: { name: string; role: string; email: string }[];
  lastContact: string; nextAction: string; nextActionDue: string;
}

const PARTNERS: Partner[] = [
  { id: "1", name: "Kongsberg Maritime", type: "strategic", category: "Technology", status: "active", owner: "Kyle Pemberton (COO)",
    agreement: { type: "NDA Rev 1.3", signed: "Feb 2026", expires: "Feb 2029", valid: true },
    workstreams: ["VOFF-CC Chemical Carrier concept (MOU)", "AIRCAT 35 K-Pos DP2 integration", "K-Chief engine monitoring for Crewliner"],
    contacts: [{ name: "Lars Eriksen", role: "Business Development", email: "l.eriksen@kongsberg.com" }, { name: "Morten Andersen", role: "Technical Director", email: "m.andersen@kongsberg.com" }],
    lastContact: "12 days ago", nextAction: "Follow up on MOU workplan", nextActionDue: "2026-04-30" },
  { id: "2", name: "Strategic Marine", type: "strategic", category: "Shipyard", status: "active", owner: "Kyle Pemberton (COO)",
    agreement: { type: "Preferred Shipyard Agreement", signed: "Jan 2026", expires: "Jan 2028", valid: true },
    workstreams: ["AIRCAT 35 Crewliner hull #3 (quote stage)", "Combi conversion kit development"],
    contacts: [{ name: "Mark Johnson", role: "Commercial Director", email: "m.johnson@strategicmarine.com" }],
    lastContact: "5 days ago", nextAction: "Review hull #3 pricing", nextActionDue: "2026-05-15" },
  { id: "3", name: "Atlantic Logistics Services (ALS)", type: "strategic", category: "In-Country Partner", status: "active", owner: "Jerome Arnold (CEO)",
    agreement: { type: "Commercial Partnership Agreement", signed: "Mar 2026", expires: "Mar 2028", valid: true },
    workstreams: ["Azule Energy tender support", "Angola port operations coordination"],
    contacts: [{ name: "Paulo Mendes", role: "Managing Director", email: "p.mendes@als-angola.com" }],
    lastContact: "3 days ago", nextAction: "Azule tender submission review", nextActionDue: "2026-04-22" },
  { id: "4", name: "Astra Trust", type: "strategic", category: "Registered Agent", status: "warm", owner: "Kyle Pemberton (COO)",
    agreement: { type: "Agency Agreement", signed: "Oct 2025", expires: "Oct 2027", valid: true },
    workstreams: ["RMI flag registration for Combi 01"],
    contacts: [{ name: "David Cho", role: "Agent", email: "d.cho@astratrust.com" }],
    lastContact: "28 days ago", nextAction: "Annual compliance review", nextActionDue: "2026-05-01" },
  { id: "5", name: "Ascenz Marorka", type: "strategic", category: "Technology", status: "warm", owner: "Kyle Pemberton (COO)",
    workstreams: ["Energy management system evaluation"],
    contacts: [{ name: "Technical Contact", role: "Sales", email: "sales@ascenz.com" }],
    lastContact: "35 days ago", nextAction: "Demo follow-up", nextActionDue: "2026-04-20" },
  { id: "6", name: "ExxonMobil Guyana Ltd.", type: "client", category: "Active Client", status: "active", owner: "Kyle Pemberton (COO)",
    agreement: { type: "Charter Party (BIMCO SUPPLYTIME)", signed: "Nov 2025", expires: "Nov 2026", valid: true },
    workstreams: ["Crewliner 01 — Liza Unity operations", "Hammerhead FPSO tender (upcoming)"],
    contacts: [{ name: "Kevin Tian", role: "Marine Logistics Manager", email: "kevin.tian@exxonmobil.com" }, { name: "Hritesh Sharma", role: "Operations Coordinator", email: "hritesh.sharma@exxonmobil.com" }],
    lastContact: "1 day ago", nextAction: "Monthly performance review", nextActionDue: "2026-04-25" },
  { id: "7", name: "Azule Energy", type: "client", category: "Active Pursuit", status: "active", owner: "Jerome Arnold (CEO)",
    workstreams: ["Block 15 CTV tender (via ALS)", "Technical evaluation in progress"],
    contacts: [{ name: "Marine Operations Team", role: "Operations", email: "marine@azule-energy.com" }],
    lastContact: "5 days ago", nextAction: "Submit technical proposal", nextActionDue: "2026-04-28" },
  { id: "8", name: "ExxonMobil Nigeria", type: "client", category: "Prospect", status: "warm", owner: "Bo Jardine (CCO)",
    workstreams: ["Erha/Usan FPSO CTV opportunity research"],
    contacts: [{ name: "Marine Dept.", role: "Marine Logistics", email: "nigeria.marine@exxonmobil.com" }],
    lastContact: "21 days ago", nextAction: "Intro meeting request", nextActionDue: "2026-05-01" },
  { id: "9", name: "American Bureau of Shipping (ABS)", type: "vendor", category: "Classification Society", status: "active", owner: "Kyle Pemberton (COO)",
    agreement: { type: "Class Services Agreement", signed: "Jan 2026", expires: "Jan 2031", valid: true },
    workstreams: ["Crewliner 01 classification", "Combi 01 classification", "Annual survey coordination"],
    contacts: [{ name: "Class Surveyor", role: "Survey Team", email: "surveys@eagle.org" }],
    lastContact: "7 days ago", nextAction: "Annual survey scheduling", nextActionDue: "2026-06-01" },
  { id: "10", name: "International Registries (IRI/RMI)", type: "vendor", category: "Flag Administration", status: "active", owner: "Kyle Pemberton (COO)",
    workstreams: ["RMI flag documentation", "Crew endorsement processing"],
    contacts: [{ name: "Flag Office", role: "Administration", email: "info@register-iri.com" }],
    lastContact: "14 days ago", nextAction: "Crew endorsement renewal batch", nextActionDue: "2026-05-15" },
  { id: "11", name: "TotalEnergies Angola", type: "prospect", category: "Prospect", status: "dormant", owner: "Bo Jardine (CCO)",
    workstreams: ["Block 32 opportunity assessment"],
    contacts: [{ name: "Marine Procurement", role: "Procurement", email: "marine@totalenergies.com" }],
    lastContact: "68 days ago", nextAction: "Re-engagement outreach", nextActionDue: "2026-04-18" },
  { id: "12", name: "Chevron Angola", type: "prospect", category: "Prospect", status: "dormant", owner: "Bo Jardine (CCO)",
    workstreams: ["Benguela Belize FPSO CTV requirement"],
    contacts: [{ name: "Marine Logistics", role: "Operations", email: "" }],
    lastContact: "52 days ago", nextAction: "Market entry research", nextActionDue: "2026-05-01" },
  { id: "13", name: "MODEC", type: "prospect", category: "Prospect", status: "warm", owner: "Kyle Pemberton (COO)",
    workstreams: ["FPSO CTV services — multi-region opportunity"],
    contacts: [{ name: "Procurement Team", role: "Marine Procurement", email: "procurement@modec.com" }],
    lastContact: "30 days ago", nextAction: "Capability presentation", nextActionDue: "2026-05-10" },
];

const statusConfig: Record<RelStatus, { label: string; cls: string }> = {
  active: { label: "ACTIVE", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  warm: { label: "WARM", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  dormant: { label: "DORMANT", cls: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  action_required: { label: "ACTION REQUIRED", cls: "bg-red-500/15 text-red-400 border-red-500/30" },
};

const PartnerHub = () => {
  const [tab, setTab] = useState("strategic");
  const [search, setSearch] = useState("");
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [showLogModal, setShowLogModal] = useState(false);

  const dormantPartners = PARTNERS.filter(p => p.status === "dormant");
  const activeNDAs = PARTNERS.filter(p => p.agreement?.valid).length;
  const actionRequired = PARTNERS.filter(p => p.status === "action_required" || p.status === "dormant").length;

  const getFiltered = (type: Partner["type"]) => {
    return PARTNERS.filter(p => p.type === type && (!search || p.name.toLowerCase().includes(search.toLowerCase())));
  };

  const renderCard = (p: Partner) => (
    <div key={p.id} className="bg-card border border-border rounded-xl overflow-hidden hover:border-[#288CC8]/30 transition-colors cursor-pointer" onClick={() => setSelectedPartner(p)}>
      {p.status === "dormant" && (
        <div className="bg-orange-500/10 border-b border-orange-500/20 px-4 py-1.5 flex items-center gap-2">
          <AlertTriangle className="w-3 h-3 text-orange-400" />
          <span className="text-[10px] text-orange-400 font-body font-bold">DORMANT — No contact in 45+ days</span>
        </div>
      )}
      <div className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h3 className="text-sm font-bold text-foreground font-body">{p.name}</h3>
            <p className="text-[10px] text-muted-foreground font-body">{p.category}</p>
          </div>
          <Badge variant="outline" className={`text-[10px] font-bold ${statusConfig[p.status].cls}`}>{statusConfig[p.status].label}</Badge>
        </div>
        <p className="text-[10px] text-muted-foreground font-body mb-3">Owner: {p.owner}</p>

        {p.agreement && (
          <div className="bg-muted/30 rounded-lg p-2.5 mb-3 border border-border">
            <p className="text-[10px] text-muted-foreground font-body">
              {p.agreement.type} · Signed {p.agreement.signed} · Expires {p.agreement.expires}
              {p.agreement.valid ? <span className="text-emerald-400 ml-1">✓ Valid</span> : <span className="text-red-400 ml-1">⚠ Expiring</span>}
            </p>
          </div>
        )}

        {p.workstreams.length > 0 && (
          <div className="mb-3">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-body font-bold mb-1">Active Workstreams</p>
            {p.workstreams.map((w, i) => (
              <p key={i} className="text-xs text-foreground font-body">• {w}</p>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between text-[10px] text-muted-foreground font-body pt-3 border-t border-border">
          <span>Last contact: {p.lastContact}</span>
          <span>Next: {p.nextActionDue}</span>
        </div>
      </div>
    </div>
  );

  return (
    <IntranetLayout>
      {/* Header */}
      <div className="bg-[#00283C] rounded-xl p-6 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div>
            <h1 className="text-[#288CC8] text-xl font-bold tracking-wider uppercase font-display">Partner Hub</h1>
            <p className="text-white/60 text-sm font-body mt-1">Client, Partner & Vendor Relationship Management</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-center">
              <p className="text-white text-lg font-bold font-display">{activeNDAs}</p>
              <p className="text-white/50 text-[10px] uppercase tracking-wider font-body">Active NDAs</p>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-lg px-4 py-2 text-center">
              <p className={`text-lg font-bold font-display ${actionRequired > 0 ? "text-amber-400" : "text-white"}`}>{actionRequired}</p>
              <p className="text-white/50 text-[10px] uppercase tracking-wider font-body">Need Attention</p>
            </div>
          </div>
        </div>
      </div>

      {/* Dormant Alert */}
      {dormantPartners.length > 0 && (
        <div className="bg-orange-500/5 border border-orange-500/20 rounded-xl p-4 mb-6">
          <h3 className="text-xs font-bold text-orange-400 font-body mb-2 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Relationships Needing Attention</h3>
          <div className="flex flex-wrap gap-2">
            {dormantPartners.map(p => (
              <button key={p.id} onClick={() => setSelectedPartner(p)} className="bg-orange-500/10 border border-orange-500/20 rounded-lg px-3 py-1.5 text-xs text-foreground font-body hover:bg-orange-500/20">
                {p.name} — {p.lastContact}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search relationships..." className="pl-9 font-body text-sm" />
        </div>
        <Button onClick={() => toast.success("Created: Add Relationship", { description: "New entry added." })} size="sm" className="bg-[#288CC8] hover:bg-[#288CC8]/80 font-body text-xs"><Plus className="w-3.5 h-3.5 mr-1" /> Add Relationship</Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="font-body mb-6">
          <TabsTrigger value="strategic"><Handshake className="w-4 h-4 mr-1.5" /> Strategic Partners</TabsTrigger>
          <TabsTrigger value="client"><Building2 className="w-4 h-4 mr-1.5" /> Clients</TabsTrigger>
          <TabsTrigger value="vendor"><Users className="w-4 h-4 mr-1.5" /> Vendors</TabsTrigger>
          <TabsTrigger value="prospect"><Target className="w-4 h-4 mr-1.5" /> Prospects</TabsTrigger>
        </TabsList>

        {(["strategic", "client", "vendor", "prospect"] as const).map(type => (
          <TabsContent key={type} value={type}>
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
              {getFiltered(type).map(renderCard)}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Partner Detail Modal */}
      <Dialog open={!!selectedPartner} onOpenChange={() => setSelectedPartner(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {selectedPartner && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display flex items-center gap-3">
                  {selectedPartner.name}
                  <Badge variant="outline" className={`text-[10px] font-bold ${statusConfig[selectedPartner.status].cls}`}>{statusConfig[selectedPartner.status].label}</Badge>
                </DialogTitle>
                <p className="text-xs text-muted-foreground font-body">{selectedPartner.category} · Owner: {selectedPartner.owner}</p>
              </DialogHeader>
              <Tabs defaultValue="overview" className="mt-4">
                <TabsList className="font-body">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="contacts">Contacts</TabsTrigger>
                  <TabsTrigger value="workstreams">Workstreams</TabsTrigger>
                  <TabsTrigger value="actions">Actions</TabsTrigger>
                </TabsList>
                <TabsContent value="overview" className="space-y-4 mt-4">
                  {selectedPartner.agreement && (
                    <div className="bg-muted/30 rounded-lg p-4 border border-border">
                      <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground font-body mb-2">Agreement</h4>
                      <div className="grid grid-cols-3 gap-3 text-xs font-body">
                        <div><span className="text-muted-foreground">Type:</span> <span className="text-foreground font-medium block">{selectedPartner.agreement.type}</span></div>
                        <div><span className="text-muted-foreground">Signed:</span> <span className="text-foreground font-medium block">{selectedPartner.agreement.signed}</span></div>
                        <div><span className="text-muted-foreground">Expires:</span> <span className={`font-medium block ${selectedPartner.agreement.valid ? "text-emerald-400" : "text-red-400"}`}>{selectedPartner.agreement.expires}</span></div>
                      </div>
                    </div>
                  )}
                  <div className="text-sm font-body">
                    <p className="text-muted-foreground">Last Contact: <span className="text-foreground font-medium">{selectedPartner.lastContact}</span></p>
                    <p className="text-muted-foreground mt-1">Next Action: <span className="text-foreground font-medium">{selectedPartner.nextAction}</span></p>
                    <p className="text-muted-foreground mt-1">Due: <span className="text-foreground font-medium">{selectedPartner.nextActionDue}</span></p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => { setShowLogModal(true); }} className="bg-[#288CC8] hover:bg-[#288CC8]/80 font-body text-xs">
                      <MessageSquare className="w-3.5 h-3.5 mr-1" /> Log Interaction
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => toast.info("Fetching intelligence brief...")} className="font-body text-xs">
                      <ExternalLink className="w-3.5 h-3.5 mr-1" /> Intelligence Brief
                    </Button>
                  </div>
                </TabsContent>
                <TabsContent value="contacts" className="mt-4">
                  <div className="space-y-3">
                    {selectedPartner.contacts.map((c, i) => (
                      <div key={i} className="bg-muted/30 rounded-lg p-3 border border-border">
                        <p className="text-sm font-medium text-foreground font-body">{c.name}</p>
                        <p className="text-xs text-muted-foreground font-body">{c.role}</p>
                        <p className="text-xs text-[#288CC8] font-body">{c.email}</p>
                      </div>
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="workstreams" className="mt-4">
                  <div className="space-y-2">
                    {selectedPartner.workstreams.map((w, i) => (
                      <div key={i} className="flex items-center gap-2 py-2 border-b border-border last:border-0">
                        <span className="w-2 h-2 rounded-full bg-[#288CC8]" />
                        <span className="text-sm font-body text-foreground">{w}</span>
                      </div>
                    ))}
                  </div>
                </TabsContent>
                <TabsContent value="actions" className="mt-4">
                  <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-sm font-body">
                      <Clock className="w-4 h-4 text-amber-400" />
                      <span className="text-foreground font-medium">{selectedPartner.nextAction}</span>
                    </div>
                    <p className="text-xs text-muted-foreground font-body mt-1">Due: {selectedPartner.nextActionDue}</p>
                  </div>
                </TabsContent>
              </Tabs>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Log Interaction Modal */}
      <Dialog open={showLogModal} onOpenChange={setShowLogModal}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display">Log Interaction</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-4">
            <Input type="date" className="font-body text-sm" />
            <Select><SelectTrigger className="font-body text-sm"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent><SelectItem value="meeting">Meeting</SelectItem><SelectItem value="call">Call</SelectItem><SelectItem value="email">Email</SelectItem><SelectItem value="event">Event</SelectItem></SelectContent></Select>
            <Input placeholder="Attendees" className="font-body text-sm" />
            <Textarea placeholder="Key points discussed..." className="font-body text-sm min-h-[80px]" />
            <Button onClick={() => { setShowLogModal(false); toast.success("Interaction logged"); }} className="w-full bg-[#288CC8] hover:bg-[#288CC8]/80 font-body">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </IntranetLayout>
  );
};

export default PartnerHub;
